// Multiple Choice Game Logic
// Types, question generation, scoring, and notebook management

// ============================================
// Type Definitions
// ============================================

export type SupportedLanguage = 'chinese' | 'russian' | 'german' | 'latin';

export type VocabularyLevel = 'beginner' | 'intermediate' | 'advanced';

export type QuestionType =
  | 'target-to-english'      // e.g., 你好 → ? (Hello)
  | 'english-to-target'      // e.g., Hello → ? (你好)
  | 'target-to-romanization' // e.g., 你好 → ? (nǐ hǎo) - Chinese/Russian only
  | 'romanization-to-target' // e.g., nǐ hǎo → ? (你好) - Chinese/Russian only
  | 'definition-to-target';  // e.g., "A greeting" → ? (你好)

// Vocabulary item structure
export interface VocabularyItem {
  id: string;                    // e.g., 'zh-beg-001'
  target: string;                // The word in target language (你好, привет)
  english: string;               // English translation
  romanization?: string;         // Pinyin/Romanization (nǐ hǎo, privet) - optional for German/Latin
  definition: string;            // Contextual definition
  partOfSpeech?: string;         // noun, verb, adjective, etc.
  example?: {                    // Example sentence
    target: string;
    english: string;
  };
}

// Dictionary with metadata
export interface MultipleChoiceDictionary {
  metadata: {
    matchingMode: 'multiple-choice';
    language: SupportedLanguage;
    level: VocabularyLevel;
    supportedModes: QuestionType[];
    totalItems: number;
    version: string;
  };
  items: VocabularyItem[];
}

// Question structure for game
export interface MultipleChoiceQuestion {
  id: string;
  questionType: QuestionType;
  prompt: string;                // What to display as the question
  promptHint?: string;           // Optional hint (e.g., part of speech)
  correctAnswer: string;         // The correct answer
  options: string[];             // Array of 4 options (including correct)
  vocabularyItem: VocabularyItem; // Reference to source item
  wasAnsweredWrong: boolean;
  wrongAttempts: number;
  userWrongAnswers: string[];    // Track what wrong answers user selected
}

// Game session state
export interface MultipleChoiceGameState {
  language: SupportedLanguage;
  level: VocabularyLevel;
  selectedTypes: QuestionType[];
  totalQuestions: number;
  currentQuestionIndex: number;
  questions: MultipleChoiceQuestion[];
  skippedQuestions: MultipleChoiceQuestion[];
  score: number;
  correctAnswers: number;
  wrongAnswers: number;
  skippedCount: number;
  startTime: number;
  endTime?: number;
  isComplete: boolean;
}

// Setup selection state
export interface MultipleChoiceSetupState {
  step: 'language' | 'level' | 'types' | 'count' | 'ready';
  language: SupportedLanguage | null;
  level: VocabularyLevel | null;
  selectedTypes: QuestionType[];
  questionCount: number;
}

// Wrong answer notebook entry
export interface WrongAnswerEntry {
  id: string;
  odUserId?: number;              // null for guests (stored in localStorage)
  vocabularyItemId: string;
  vocabularyItem: VocabularyItem;
  language: SupportedLanguage;
  level: VocabularyLevel;
  questionType: QuestionType;
  wrongAttempts: number;
  userWrongAnswers: string[];
  correctAfterReview: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Language Configuration
// ============================================

export const LANGUAGE_CONFIG: Record<SupportedLanguage, {
  name: string;
  nativeName: string;
  flag: string;
  hasRomanization: boolean;
  available: boolean;
}> = {
  chinese: {
    name: 'Chinese',
    nativeName: '中文',
    flag: '🇨🇳',
    hasRomanization: true,
    available: true
  },
  russian: {
    name: 'Russian',
    nativeName: 'Русский',
    flag: '🇷🇺',
    hasRomanization: true,
    available: true
  },
  german: {
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    hasRomanization: false,
    available: true
  },
  latin: {
    name: 'Latin',
    nativeName: 'Latina',
    flag: '🏛️',
    hasRomanization: false,
    available: true
  }
};

export const LEVEL_CONFIG: Record<VocabularyLevel, {
  name: string;
  description: string;
  icon: string;
}> = {
  beginner: {
    name: 'Beginner',
    description: 'Basic vocabulary and common phrases',
    icon: '🌱'
  },
  intermediate: {
    name: 'Intermediate',
    description: 'Everyday conversations and expressions',
    icon: '🌿'
  },
  advanced: {
    name: 'Advanced',
    description: 'Complex vocabulary and idioms',
    icon: '🌳'
  }
};

export const QUESTION_TYPE_CONFIG: Record<QuestionType, {
  name: string;
  description: string;
  icon: string;
  requiresRomanization: boolean;
}> = {
  'target-to-english': {
    name: 'Target → English',
    description: 'See target language, choose English meaning',
    icon: '🎯',
    requiresRomanization: false
  },
  'english-to-target': {
    name: 'English → Target',
    description: 'See English, choose target language word',
    icon: '🔄',
    requiresRomanization: false
  },
  'target-to-romanization': {
    name: 'Target → Romanization',
    description: 'See target language, choose pronunciation',
    icon: '🔤',
    requiresRomanization: true
  },
  'romanization-to-target': {
    name: 'Romanization → Target',
    description: 'See pronunciation, choose target language word',
    icon: '📝',
    requiresRomanization: true
  },
  'definition-to-target': {
    name: 'Definition → Target',
    description: 'See definition, choose target language word',
    icon: '📖',
    requiresRomanization: false
  }
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get supported question types for a language
 */
export function getSupportedQuestionTypes(language: SupportedLanguage): QuestionType[] {
  const hasRomanization = LANGUAGE_CONFIG[language].hasRomanization;

  const types: QuestionType[] = [
    'target-to-english',
    'english-to-target',
    'definition-to-target'
  ];

  if (hasRomanization) {
    types.push('target-to-romanization', 'romanization-to-target');
  }

  return types;
}

/**
 * Shuffle array in place (Fisher-Yates algorithm)
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generate distractor options (wrong answers)
 */
function generateDistractors(
  correctAnswer: string,
  pool: string[],
  count: number = 3
): string[] {
  // Filter out the correct answer and empty strings
  const available = pool.filter(item => item && item !== correctAnswer);

  // Shuffle and take 'count' items
  const shuffled = shuffleArray(available);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Create a single question from a vocabulary item
 */
export function createQuestion(
  item: VocabularyItem,
  questionType: QuestionType,
  allItems: VocabularyItem[]
): MultipleChoiceQuestion {
  let prompt: string;
  let promptHint: string | undefined;
  let correctAnswer: string;
  let distractorPool: string[];

  switch (questionType) {
    case 'target-to-english':
      prompt = item.target;
      promptHint = item.romanization;
      correctAnswer = item.english;
      distractorPool = allItems.map(i => i.english);
      break;

    case 'english-to-target':
      prompt = item.english;
      promptHint = item.partOfSpeech;
      correctAnswer = item.target;
      distractorPool = allItems.map(i => i.target);
      break;

    case 'target-to-romanization':
      prompt = item.target;
      correctAnswer = item.romanization || '';
      distractorPool = allItems.filter(i => i.romanization).map(i => i.romanization!);
      break;

    case 'romanization-to-target':
      prompt = item.romanization || '';
      correctAnswer = item.target;
      distractorPool = allItems.map(i => i.target);
      break;

    case 'definition-to-target':
      prompt = item.definition;
      promptHint = item.partOfSpeech;
      correctAnswer = item.target;
      distractorPool = allItems.map(i => i.target);
      break;

    default:
      throw new Error(`Unknown question type: ${questionType}`);
  }

  // Generate 3 distractors (wrong options)
  const distractors = generateDistractors(correctAnswer, distractorPool, 3);

  // If we don't have enough distractors, pad with placeholder (shouldn't happen with 50+ items)
  while (distractors.length < 3) {
    distractors.push(`Option ${distractors.length + 2}`);
  }

  // Combine and shuffle options
  const options = shuffleArray([correctAnswer, ...distractors]);

  return {
    id: `q-${item.id}-${questionType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    questionType,
    prompt,
    promptHint,
    correctAnswer,
    options,
    vocabularyItem: item,
    wasAnsweredWrong: false,
    wrongAttempts: 0,
    userWrongAnswers: []
  };
}

/**
 * Generate a set of multiple choice questions
 * Ensures different vocabulary items are used for each question with full randomization
 */
export function generateQuestions(
  items: VocabularyItem[],
  selectedTypes: QuestionType[],
  count: number
): MultipleChoiceQuestion[] {
  if (items.length === 0 || selectedTypes.length === 0) {
    return [];
  }

  const questions: MultipleChoiceQuestion[] = [];
  const usedItemIds = new Set<string>();

  // Calculate how many questions per type (distribute evenly)
  const questionsPerType = Math.ceil(count / selectedTypes.length);

  // For each question type, randomly select items
  for (const questionType of selectedTypes) {
    // Determine which items are valid for this question type
    const needsRomanization = questionType.includes('romanization');
    const validItems = needsRomanization
      ? items.filter(item => item.romanization)
      : items;

    // Get items that haven't been used yet
    let availableItems = validItems.filter(item => !usedItemIds.has(item.id));

    // Shuffle available items for random selection
    availableItems = shuffleArray(availableItems);

    let questionsCreatedForType = 0;

    for (const item of availableItems) {
      if (questionsCreatedForType >= questionsPerType || questions.length >= count) {
        break;
      }

      usedItemIds.add(item.id);
      const question = createQuestion(item, questionType, items);
      questions.push(question);
      questionsCreatedForType++;
    }

    // If we need more questions but ran out of unique items, allow reuse
    if (questionsCreatedForType < questionsPerType && questions.length < count) {
      // Get items not yet used for THIS specific question type
      const reusableItems = shuffleArray(validItems).filter(item =>
        !questions.some(q => q.vocabularyItem.id === item.id && q.questionType === questionType)
      );

      for (const item of reusableItems) {
        if (questionsCreatedForType >= questionsPerType || questions.length >= count) {
          break;
        }

        const question = createQuestion(item, questionType, items);
        questions.push(question);
        questionsCreatedForType++;
      }
    }

    if (questions.length >= count) break;
  }

  // Final shuffle to randomize the order of questions
  return shuffleArray(questions).slice(0, count);
}

/**
 * Initialize a new game state
 */
export function initializeGameState(
  language: SupportedLanguage,
  level: VocabularyLevel,
  selectedTypes: QuestionType[],
  questions: MultipleChoiceQuestion[]
): MultipleChoiceGameState {
  return {
    language,
    level,
    selectedTypes,
    totalQuestions: questions.length,
    currentQuestionIndex: 0,
    questions,
    skippedQuestions: [],
    score: 0,
    correctAnswers: 0,
    wrongAnswers: 0,
    skippedCount: 0,
    startTime: Date.now(),
    isComplete: false
  };
}

/**
 * Initialize setup state
 */
export function initializeSetupState(): MultipleChoiceSetupState {
  return {
    step: 'language',
    language: null,
    level: null,
    selectedTypes: [],
    questionCount: 10
  };
}

// ============================================
// Scoring Functions
// ============================================

/**
 * Handle correct answer
 */
export function handleCorrectAnswer(state: MultipleChoiceGameState): MultipleChoiceGameState {
  const newState = { ...state };
  newState.score += 1;
  newState.correctAnswers += 1;
  newState.currentQuestionIndex += 1;

  // Check if we need to process skipped questions
  if (newState.currentQuestionIndex >= newState.questions.length && newState.skippedQuestions.length > 0) {
    // Move skipped questions to main queue
    newState.questions = [...newState.questions, ...newState.skippedQuestions];
    newState.skippedQuestions = [];
  }

  // Check for completion
  if (newState.currentQuestionIndex >= newState.questions.length) {
    newState.isComplete = true;
    newState.endTime = Date.now();
  }

  return newState;
}

/**
 * Handle wrong answer
 */
export function handleWrongAnswer(
  state: MultipleChoiceGameState,
  selectedAnswer: string
): MultipleChoiceGameState {
  const newState = { ...state };
  newState.score -= 1; // No floor - can go negative
  newState.wrongAnswers += 1;

  // Mark current question as having wrong attempt
  const currentQuestion = newState.questions[newState.currentQuestionIndex];
  if (currentQuestion) {
    currentQuestion.wasAnsweredWrong = true;
    currentQuestion.wrongAttempts += 1;
    currentQuestion.userWrongAnswers.push(selectedAnswer);
  }

  return newState;
}

/**
 * Handle skip
 */
export function handleSkip(state: MultipleChoiceGameState): MultipleChoiceGameState {
  const newState = { ...state };
  const currentQuestion = newState.questions[newState.currentQuestionIndex];

  if (currentQuestion) {
    newState.skippedQuestions.push(currentQuestion);
    newState.skippedCount += 1;
  }

  newState.currentQuestionIndex += 1;

  // Check if we need to process skipped questions
  if (newState.currentQuestionIndex >= newState.questions.length && newState.skippedQuestions.length > 0) {
    // Move skipped questions to main queue
    newState.questions = [...newState.questions, ...newState.skippedQuestions];
    newState.skippedQuestions = [];
  }

  // Check for completion
  if (newState.currentQuestionIndex >= newState.questions.length) {
    newState.isComplete = true;
    newState.endTime = Date.now();
  }

  return newState;
}

/**
 * Get current question
 */
export function getCurrentQuestion(state: MultipleChoiceGameState): MultipleChoiceQuestion | null {
  if (state.currentQuestionIndex >= state.questions.length) {
    return null;
  }
  return state.questions[state.currentQuestionIndex];
}

/**
 * Calculate game statistics
 */
export function calculateGameStats(state: MultipleChoiceGameState): {
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  skippedCount: number;
  score: number;
  accuracy: number;
  timeTaken: number;
  wrongQuestions: MultipleChoiceQuestion[];
} {
  const timeTaken = state.endTime
    ? Math.round((state.endTime - state.startTime) / 1000)
    : Math.round((Date.now() - state.startTime) / 1000);

  const totalAttempts = state.correctAnswers + state.wrongAnswers;
  const accuracy = totalAttempts > 0
    ? Math.round((state.correctAnswers / state.totalQuestions) * 100)
    : 0;

  const wrongQuestions = state.questions.filter(q => q.wasAnsweredWrong);

  return {
    totalQuestions: state.totalQuestions,
    correctAnswers: state.correctAnswers,
    wrongAnswers: state.wrongAnswers,
    skippedCount: state.skippedCount,
    score: state.score,
    accuracy,
    timeTaken,
    wrongQuestions
  };
}

// ============================================
// Notebook Functions
// ============================================

const NOTEBOOK_STORAGE_KEY = 'multipleChoiceNotebook';

/**
 * Save wrong answer to localStorage (for guests)
 */
export function saveWrongAnswerLocally(
  question: MultipleChoiceQuestion,
  language: SupportedLanguage,
  level: VocabularyLevel
): void {
  try {
    const stored = localStorage.getItem(NOTEBOOK_STORAGE_KEY);
    const notebook: WrongAnswerEntry[] = stored ? JSON.parse(stored) : [];

    // Check if entry exists
    const existingIndex = notebook.findIndex(
      e => e.vocabularyItemId === question.vocabularyItem.id && e.questionType === question.questionType
    );

    const now = new Date().toISOString();

    if (existingIndex >= 0) {
      // Update existing entry
      notebook[existingIndex].wrongAttempts += question.wrongAttempts;
      notebook[existingIndex].userWrongAnswers = [
        ...notebook[existingIndex].userWrongAnswers,
        ...question.userWrongAnswers
      ];
      notebook[existingIndex].updatedAt = now;
    } else {
      // Add new entry
      const entry: WrongAnswerEntry = {
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        vocabularyItemId: question.vocabularyItem.id,
        vocabularyItem: question.vocabularyItem,
        language,
        level,
        questionType: question.questionType,
        wrongAttempts: question.wrongAttempts,
        userWrongAnswers: question.userWrongAnswers,
        correctAfterReview: false,
        createdAt: now,
        updatedAt: now
      };
      notebook.push(entry);
    }

    localStorage.setItem(NOTEBOOK_STORAGE_KEY, JSON.stringify(notebook));
  } catch (error) {
    console.error('Failed to save wrong answer locally:', error);
  }
}

/**
 * Get wrong answers from localStorage
 */
export function getLocalWrongAnswers(
  language?: SupportedLanguage,
  level?: VocabularyLevel
): WrongAnswerEntry[] {
  try {
    const stored = localStorage.getItem(NOTEBOOK_STORAGE_KEY);
    if (!stored) return [];

    let entries: WrongAnswerEntry[] = JSON.parse(stored);

    if (language) {
      entries = entries.filter(e => e.language === language);
    }
    if (level) {
      entries = entries.filter(e => e.level === level);
    }

    return entries.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  } catch (error) {
    console.error('Failed to get local wrong answers:', error);
    return [];
  }
}

/**
 * Clear a wrong answer from localStorage
 */
export function clearLocalWrongAnswer(id: string): void {
  try {
    const stored = localStorage.getItem(NOTEBOOK_STORAGE_KEY);
    if (!stored) return;

    const notebook: WrongAnswerEntry[] = JSON.parse(stored);
    const filtered = notebook.filter(e => e.id !== id);
    localStorage.setItem(NOTEBOOK_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to clear wrong answer:', error);
  }
}

/**
 * Mark wrong answer as learned in localStorage
 */
export function markAsLearned(id: string): void {
  try {
    const stored = localStorage.getItem(NOTEBOOK_STORAGE_KEY);
    if (!stored) return;

    const notebook: WrongAnswerEntry[] = JSON.parse(stored);
    const index = notebook.findIndex(e => e.id === id);

    if (index >= 0) {
      notebook[index].correctAfterReview = true;
      notebook[index].updatedAt = new Date().toISOString();
      localStorage.setItem(NOTEBOOK_STORAGE_KEY, JSON.stringify(notebook));
    }
  } catch (error) {
    console.error('Failed to mark as learned:', error);
  }
}

/**
 * Clear all local wrong answers
 */
export function clearAllLocalWrongAnswers(): void {
  try {
    localStorage.removeItem(NOTEBOOK_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear all wrong answers:', error);
  }
}
